import React, { useState } from 'react';
import { Translate, withLocalize } from 'react-localize-redux';
import PropTypes from 'prop-types';
import Tooltip from '../../../../components/molecules/Tooltip';
import { Label, Button, TextField, RadioButtonsGroup, WarningMessage } from '../../../../components/widgets';
import css from './AdvancedPostbackSettings.css';
import { organizationOptionsShape, partnerShape, templateShape } from '../../types';
import PostbackFilters, { POSTBACK_FILTERS_TYPES } from './PostbackFilters';
import SettingsIcon from '../../../../resources/svg/settings-slider.svg';
import { trackPartnerConfigMixpanelEvent } from '../utils';

function AdvancedPostbackSettings({
    onAccept = () => {},
    onReject = () => {},
    eventName = null,
    postback,
    partner,
    isReadonly = false,
    orgOptions,
    template,
    translate,
    flexiblePostbackReadOnly = false,
    availableFilters = [],
}) {
    const [campaignRegex, setCampaignRegex] = useState(postback.campaignRegex);
    const [customByUser, setCustomByUser] = useState(postback.customByUser || false);
    const [postbackUri, setPostbackUri] = useState(postback.postbackUri);
    const [filters, setFilters] = useState(postback.filters || []);
    const [filterErrors, setFilterErrors] = useState([]);
    const supportCampaignRegex =
        orgOptions.trackerCampaignNameFilterSupported || orgOptions.advancedPostbackEditingSupported;
    const supportFlexiblePostback = orgOptions.flexiblePostbackSupported || orgOptions.advancedPostbackEditingSupported;

    const validateFilters = () => {
        if (!filters || filters.length === 0) {
            return true;
        }

        const errors = filters.map(filter => {
            const filterDef = availableFilters.find(f => f.name === filter.filter);

            // Check if it's a custom filter (not in availableFilters)
            const isCustomFilter = !filterDef && filter.filter;

            // Validate custom filter format: must start with { and end with }
            const hasValidFormat = isCustomFilter ? filter.filter.startsWith('{') && filter.filter.endsWith('}') : true;

            const isMultiSelect = filterDef?.type === POSTBACK_FILTERS_TYPES.MULTI_SELECT || isCustomFilter;
            const hasFilterValue = isMultiSelect
                ? Array.isArray(filter.filterValue) && filter.filterValue.length > 0
                : filter.filterValue !== null && filter.filterValue !== undefined && filter.filterValue !== '';

            return {
                filter: !filter.filter || !hasValidFormat,
                filterValue: !hasFilterValue,
            };
        });

        const hasErrors = errors.some(error => error.filter || error.filterValue);
        setFilterErrors(errors);
        return !hasErrors;
    };

    const handleAccept = () => {
        if (validateFilters()) {
            const previousFilterCount = (postback.filters || []).length;
            const currentFilterCount = filters.length;

            if (previousFilterCount !== currentFilterCount) {
                const action =
                    previousFilterCount < currentFilterCount ? 'Postback filter added' : 'Postback filter removed';
                trackPartnerConfigMixpanelEvent(action, partner);
            }

            onAccept(campaignRegex, customByUser, postbackUri, filters);
        }
    };

    return (
        <div className={css.wrapper}>
            <div className={css.container}>
                <div className={css.title}>
                    <SettingsIcon />
                    <Translate
                        id="STATIC.PAGES.PARTNER_CONFIGURATION.SHELF.POSTBACK_POP_UP.TITLE"
                        data={{ eventName }}
                    />
                </div>
                {supportFlexiblePostback && (
                    <div className={css.postbackField}>
                        <div className={css.text}>
                            <Label
                                text="STATIC.PAGES.PARTNER_CONFIGURATION.SHELF.POSTBACK_POP_UP.POSTBACK_URL"
                                type="h3"
                                tooltip="STATIC.PAGES.PARTNER_CONFIGURATION.SHELF.POSTBACK_POP_UP.POSTBACK_URL_TOOLTIP"
                                className={css.label}
                            />
                            <RadioButtonsGroup
                                disabled={flexiblePostbackReadOnly}
                                itemsClassName={css.radioButton}
                                radioItems={[
                                    {
                                        label:
                                            'STATIC.PAGES.PARTNER_CONFIGURATION.SHELF.POSTBACK_POP_UP.DEFAULT_POSTBACK',
                                        tooltip: translate(
                                            'STATIC.PAGES.PARTNER_CONFIGURATION.SHELF.POSTBACK_POP_UP.DEFAULT_POSTBACK_TOOLTIP',
                                            { partnerName: partner.displayName }
                                        ),
                                        checked: !customByUser,
                                        id: 'default-postback',
                                    },
                                    {
                                        label:
                                            'STATIC.PAGES.PARTNER_CONFIGURATION.SHELF.POSTBACK_POP_UP.MANUAL_POSTBACK',
                                        tooltip:
                                            'STATIC.PAGES.PARTNER_CONFIGURATION.SHELF.POSTBACK_POP_UP.MANUAL_POSTBACK_TOOLTIP',
                                        checked: customByUser,
                                        id: 'manual-postback',
                                    },
                                ]}
                                onChange={() => setCustomByUser(!customByUser)}
                            />
                        </div>
                        <div>
                            <Tooltip
                                interactive
                                titleTranslationKey="STATIC.PAGES.PARTNER_CONFIGURATION.SHELF.POSTBACK_POP_UP.MANUAL_POSTBACK_HOVER"
                                disabled={!flexiblePostbackReadOnly}
                            >
                                <TextField
                                    inputConfig={
                                        customByUser
                                            ? {}
                                            : {
                                                  style: {
                                                      resize: 'none',
                                                  },
                                              }
                                    }
                                    type="textarea"
                                    value={!customByUser ? template.templateUri : postbackUri || template.templateUri}
                                    containerClass={css.input}
                                    inputClassName={css.postback}
                                    disabled={flexiblePostbackReadOnly || !customByUser}
                                    onChange={setPostbackUri}
                                />
                            </Tooltip>
                            <WarningMessage
                                className={css.note}
                                show={customByUser}
                                showIcon={false}
                                showTypeIcon
                                type="info"
                                message="STATIC.PAGES.PARTNER_CONFIGURATION.SHELF.POSTBACK_POP_UP.MANUAL_POSTBACK_NOTE"
                            />
                        </div>
                    </div>
                )}
                <WarningMessage
                    show={postback.sendAll && (filters.length > 0 || (campaignRegex || '') !== '')}
                    message="STATIC.PAGES.PARTNER_CONFIGURATION.SHELF.POSTBACK_POP_UP.SEND_ALL_FILTERS_MESSAGE"
                    showIcon
                    type="warning"
                />
                {supportCampaignRegex && (
                    <Tooltip
                        titleTranslationKey="STATIC.PAGES.PARTNER_CONFIGURATION.SHELF.POSTBACK_POP_UP.DISABLED_CAMPAIGN_FILTER_MESSAGE"
                        disabled={!postback.sendAll || (campaignRegex || '') !== ''}
                    >
                        <TextField
                            placeholder="(none)"
                            value={campaignRegex || ''}
                            onChange={setCampaignRegex}
                            label="STATIC.PAGES.PARTNER_CONFIGURATION.SHELF.POSTBACK_POP_UP.TRACKER_CAMPAIGN_NAME_FILTER"
                            labelTooltip="STATIC.PAGES.PARTNER_CONFIGURATION.SHELF.POSTBACK_POP_UP.TRACKER_CAMPAIGN_NAME_FILTER_TOOLTIP"
                            labelClass={css.postbackField}
                            labelTextClass={css.text}
                            inputClassName={css.input}
                            disabled={postback.sendAll || isReadonly}
                        />
                    </Tooltip>
                )}
                {availableFilters.length > 0 && (
                    <PostbackFilters
                        filters={filters}
                        availableFilters={availableFilters}
                        onFiltersChange={setFilters}
                        disabled={postback.sendAll || isReadonly}
                        errors={filterErrors}
                        sendAll={postback.sendAll}
                    />
                )}
            </div>
            {!flexiblePostbackReadOnly && (
                <div className={css.actionButtons}>
                    <Button type="flat" text="STATIC.BUTTONS.CANCEL" onClick={onReject} />
                    <Button text="STATIC.BUTTONS.DONE" onClick={handleAccept} className={css.acceptButton} />
                </div>
            )}
        </div>
    );
}

AdvancedPostbackSettings.propTypes = {
    onReject: PropTypes.func,
    onAccept: PropTypes.func,
    eventName: PropTypes.string,
    isReadonly: PropTypes.bool,
    postback: PropTypes.objectOf(PropTypes.any).isRequired,
    partner: partnerShape.isRequired,
    orgOptions: organizationOptionsShape.isRequired,
    template: templateShape.isRequired,
    translate: PropTypes.func.isRequired,
    flexiblePostbackReadOnly: PropTypes.bool,
    availableFilters: PropTypes.arrayOf(
        PropTypes.shape({
            name: PropTypes.string.isRequired,
            displayName: PropTypes.string,
            type: PropTypes.oneOf(Object.values(POSTBACK_FILTERS_TYPES)),
        })
    ),
};

export default withLocalize(AdvancedPostbackSettings);
